import { AppIdentityCompositeEntity } from '@nk/data-entities-lib';
import posthog from 'posthog-js';
import { Location } from '@angular/common';

export function registerIdentityWithPosthog(identity: AppIdentityCompositeEntity): void {
  // TODO: We are getting a lot of "Unidentified x@enin.ai". I am wondering if this is because we use membership uuid as
  //  the identifying id. And that POSTHOG doesn't like there being multiple IDs per email. Maybe we can switch the REAL
  //  ID to the email. This means if someone has access to two customers, they will be viewed as the same person.
  posthog.identify(identity.app_membership.uuid, {
    email: identity.app_user.email, // Needs to be included because `email` is a special property in posthogs.

    /* eslint-disable @typescript-eslint/naming-convention */
    user__uuid: identity.app_user.uuid,
    user__name: identity.app_user.name,
    user__language_code: identity.app_user.language_code,
    user__admin_flag: identity.app_user.admin_flag,

    membership__uuid: identity.app_membership.uuid,
    membership__authorized_email: identity.app_membership.app_user_email, // `authorized_email` is correct, since `app_user_email` will be deprecated.
    membership__disabled_flag: identity.app_membership.disabled_flag,
    membership__app_invite_status_key: identity.app_membership.app_invite_status_key,
    membership__invited_by_app_membership_uuid: identity.app_membership.invited_by_app_membership_uuid,
    membership__invited_by_app_user_email: identity.app_membership.invited_by_app_user_email,
    membership__privacy_signature_timestamp: identity.app_membership.privacy_signature_timestamp,
    membership__marketing_signature_timestamp: identity.app_membership.marketing_signature_timestamp,
    membership__app_interface_mode_uuid: identity.app_membership.app_interface_mode_uuid,
    membership__privacy_content_hash_uuid: identity.app_membership.privacy_content_hash_uuid,

    customer__uuid: identity.app_customer.uuid,
    customer__name: identity.app_customer.name,
    customer__slug: identity.app_customer.slug,
    customer__desensitize_flag: identity.app_customer.desensitize_flag,
    customer__tos_signature_timestamp: identity.app_customer.tos_signature_timestamp,
    customer__subscription_account_id: identity.app_customer.subscription_account_id,
    customer__tos_content_hash_uuid: identity.app_customer.tos_content_hash_uuid,

    customer_details__app_customer_industry_key: identity.app_customer_details?.app_customer_industry_key,
    customer_details__company_uuid: identity.app_customer_details?.company_uuid,
    customer_details__org_nr_schema: identity.app_customer_details?.org_nr_schema,
    customer_details__org_nr: identity.app_customer_details?.org_nr,

    /* eslint-enable */
  });
}

interface Error {
  error: IArguments;
  date: Date;
}
let errors: Error[] = [];
const ERROR_LIMIT = 20;

function accumulateError(error: IArguments, errors: Error[]): Error[] {
  const result = [...errors, { error, date: new Date() }];
  // Trim the array to keep only the last 20 errors
  if (result.length > ERROR_LIMIT) {
    return result.slice(1);
  }
  return result;
}

function checkSuspiciousUrlChange(previousUrl: string, currentUrl: string): boolean {
  // it can jump from company page or company browser to dashboard
  return (
    (previousUrl.startsWith('/company?app_customer_uuid=') || previousUrl.startsWith('/company/')) &&
    ['', '/', 'dashboard'].includes(currentUrl) &&
    !currentUrl.includes('?rbc=true')
  );
}

/**
 * Temporary solution to catch odd sporadically redirect error
 */
export function registerRedirectErrorCatcher(location: Location): void {
  console.error = ((originLogFn) =>
    function () {
      // eslint-disable-next-line prefer-rest-params
      const error = arguments;
      errors = accumulateError(error, errors);
      originLogFn.apply(this, error);
    })(console.error);
  let previousUrl = window.location.href;
  location.onUrlChange((currentUrl) => {
    if (checkSuspiciousUrlChange(previousUrl, currentUrl)) {
      posthog.capture('$suspiciousurlchange', {
        previousUrl,
        currentUrl,
        errors,
      });
    }
    previousUrl = currentUrl;
  });
}

const UUID_REGEX = /\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/gi;
const UUID_NAME_REGEX = /\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/[^/]+/gi;
const REPLACEMENT = '/UUID';

/**
 * Takes URL and replaces uuid with a generic replacement.
 * It searches and replaces uuid and name combination first, if not found it looks after single uuid too.
 */
export function getPageTypeFromUrl(url: string): string | null {
  if (url.startsWith('/company?app_customer_uuid=')) {
    return '/company/browser';
  }
  return url.replace(UUID_NAME_REGEX, REPLACEMENT).replace(UUID_REGEX, REPLACEMENT);
}
